
import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, Filter } from 'lucide-react';
import { GalleryFilters } from '@/types/gallery';

interface GalleryFiltersProps {
  filters: GalleryFilters;
  onFilterChange: (filters: Partial<GalleryFilters>) => void;
  clinics?: { id: string; name: string }[];
  treatmentTypes?: string[];
}

const GalleryFiltersComponent: React.FC<GalleryFiltersProps> = ({ 
  filters, 
  onFilterChange,
  clinics = [],
  treatmentTypes = []
}) => {
  const handleResetFilters = () => {
    onFilterChange({
      visibility: 'all',
      sortBy: 'date',
      sortDirection: 'desc',
      clinicId: undefined,
      treatmentTypes: []
    });
  };

  const toggleTreatmentType = (type: string) => {
    const currentTypes = filters.treatmentTypes || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter(t => t !== type)
      : [...currentTypes, type];
    
    onFilterChange({ treatmentTypes: newTypes });
  };
  
  const hasActiveFilters = filters.visibility !== 'all' || 
                          filters.clinicId !== undefined ||
                          (filters.treatmentTypes && filters.treatmentTypes.length > 0);

  return (
    <div className="bg-muted/30 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium flex items-center">
          <Filter className="h-4 w-4 mr-2" />
          Filters & Sorting
        </h3>
        
        {hasActiveFilters && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleResetFilters}
            className="h-8 text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            Reset Filters
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {/* Visibility Filter */}
        <div>
          <label className="text-sm font-medium mb-1 block">Visibility</label>
          <Select 
            value={filters.visibility} 
            onValueChange={(value) => onFilterChange({ visibility: value as 'all' | 'public' | 'private' })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cases</SelectItem>
              <SelectItem value="public">Public Only</SelectItem>
              <SelectItem value="private">Private Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Clinic Filter */}
        <div>
          <label className="text-sm font-medium mb-1 block">Clinic</label>
          <Select 
            value={filters.clinicId || "all"} 
            onValueChange={(value) => onFilterChange({ clinicId: value === "all" ? undefined : value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Clinics" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clinics</SelectItem>
              {clinics.map(clinic => (
                <SelectItem key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Sort By */}
        <div>
          <label className="text-sm font-medium mb-1 block">Sort By</label>
          <Select 
            value={filters.sortBy} 
            onValueChange={(value) => onFilterChange({ sortBy: value as 'date' | 'title' })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Date" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Date Updated</SelectItem>
              <SelectItem value="title">Title</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Sort Direction */}
        <div>
          <label className="text-sm font-medium mb-1 block">Sort Order</label>
          <Select 
            value={filters.sortDirection} 
            onValueChange={(value) => onFilterChange({ sortDirection: value as 'asc' | 'desc' })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Descending" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Newest First</SelectItem>
              <SelectItem value="asc">Oldest First</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Treatment Types Filter */}
      {treatmentTypes.length > 0 && (
        <div className="mt-4">
          <label className="text-sm font-medium mb-2 block">Treatment Types</label>
          <div className="flex flex-wrap gap-2">
            {treatmentTypes.map((type) => (
              <Badge 
                key={type}
                variant={(filters.treatmentTypes || []).includes(type) ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => toggleTreatmentType(type)}
              >
                {(filters.treatmentTypes || []).includes(type) && '✓ '}
                {type}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {hasActiveFilters && (
        <div className="pt-2 text-xs text-muted-foreground">
          {filters.visibility !== 'all' && (
            <span className="inline-block mr-3">
              <Badge variant="secondary" className="mr-1">{filters.visibility}</Badge>
            </span>
          )}
          
          {filters.clinicId && (
            <span className="inline-block mr-3">
              <Badge variant="secondary" className="mr-1">
                {clinics.find(c => c.id === filters.clinicId)?.name || 'Clinic'}
              </Badge>
            </span>
          )}
          
          {filters.treatmentTypes && filters.treatmentTypes.length > 0 && (
            <span className="inline-block">
              <span className="text-muted-foreground mr-1">Types:</span>
              {filters.treatmentTypes.map(type => (
                <Badge key={type} variant="secondary" className="mr-1">{type}</Badge>
              ))}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default GalleryFiltersComponent;
